import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import { Icon } from "@iconify/react";
import { COUNTRY_OPTIONS, normalizeCountryCode } from "../../utils/countries";

export const stripeCardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#1f2937",
      fontFamily:
        'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: "24px",
      "::placeholder": {
        color: "#9ca3af",
        fontWeight: "400",
      },
      iconColor: "#4ECDC4",
    },
    invalid: {
      color: "#dc3545",
      iconColor: "#dc3545",
    },
    complete: {
      color: "#1f2937",
      iconColor: "#2b7a78",
    },
  },
};

const sectionStyles = `
  .pondstat-stripe-section {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);
    border: 1px solid #e8ecef;
    overflow: hidden;
  }
  .pondstat-stripe-section__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px 0;
  }
  .pondstat-stripe-section__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(78, 205, 196, 0.15), rgba(43, 122, 120, 0.1));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2b7a78;
    flex-shrink: 0;
  }
  .pondstat-stripe-section__title {
    font-size: 17px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  .pondstat-stripe-section__subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
  }
  .pondstat-stripe-section__body {
    padding: 20px 24px 24px;
  }
  .pondstat-stripe-section__field-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 10px;
  }
  .pondstat-stripe-section__card-wrap {
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px 18px;
    background: #fafbfc;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .pondstat-stripe-section__card-wrap:focus-within {
    border-color: #4ECDC4;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(78, 205, 196, 0.12);
  }
  .pondstat-stripe-section__hints {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 12px;
    font-size: 12px;
    color: #9ca3af;
  }
  .pondstat-stripe-section__hints span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .pondstat-stripe-section__pay-btn {
    width: 100%;
    margin-top: 20px;
    padding: 16px 20px;
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, #4ECDC4 0%, #3bb5ad 100%);
    box-shadow: 0 6px 20px rgba(78, 205, 196, 0.35);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  }
  .pondstat-stripe-section__pay-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(78, 205, 196, 0.45);
  }
  .pondstat-stripe-section__pay-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: #cbd5e1;
  }
  .pondstat-stripe-section__cancel {
    width: 100%;
    margin-top: 12px;
    padding: 10px;
    border: none;
    background: transparent;
    color: #6b7280;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 10px;
    transition: color 0.15s ease, background 0.15s ease;
  }
  .pondstat-stripe-section__cancel:hover {
    color: #374151;
    background: #f3f4f6;
  }
  .pondstat-stripe-section__footer {
    text-align: center;
    margin-top: 16px;
    font-size: 12px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .pondstat-stripe-section__error {
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 14px;
  }
  .pondstat-stripe-section__billing-box {
    background: #f8fafc;
    border: 1px solid #e8ecef;
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 16px;
    font-size: 13px;
  }
  .pondstat-stripe-section__billing-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
  }
  .pondstat-stripe-section__billing-row:last-child {
    margin-bottom: 0;
  }
  .pondstat-stripe-section__recurring {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
    color: #2b7a78;
    font-size: 12px;
    font-weight: 600;
  }
  .pondstat-stripe-section__coupon {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
  }
  .pondstat-stripe-section__coupon-row {
    display: flex;
    gap: 8px;
  }
  .pondstat-stripe-section__coupon-row input {
    flex: 1;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 14px;
  }
  .pondstat-stripe-section__coupon-row input:focus {
    outline: none;
    border-color: #4ECDC4;
    box-shadow: 0 0 0 4px rgba(78, 205, 196, 0.12);
  }
  .pondstat-stripe-section__coupon-btn {
    border: none;
    border-radius: 12px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    background: #2b7a78;
    color: #fff;
    cursor: pointer;
    white-space: nowrap;
  }
  .pondstat-stripe-section__coupon-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .pondstat-stripe-section__coupon-msg {
    margin-top: 8px;
    font-size: 13px;
  }
  .pondstat-stripe-section__coupon-msg--success {
    color: #059669;
  }
  .pondstat-stripe-section__coupon-msg--error {
    color: #dc3545;
  }
  .pondstat-stripe-section__discount-box {
    background: #f0fdfa;
    border: 1px solid rgba(78, 205, 196, 0.25);
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 16px;
  }
  .pondstat-stripe-section__discount-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    margin-bottom: 6px;
  }
  .pondstat-stripe-section__discount-row:last-child {
    margin-bottom: 0;
  }
  .pondstat-stripe-section__discount-row--muted {
    color: #6b7280;
    text-decoration: line-through;
  }
  .pondstat-stripe-section__discount-row--savings {
    color: #059669;
    font-weight: 600;
  }
  .pondstat-country-picker {
    position: relative;
    margin-bottom: 14px;
  }
  .pondstat-country-picker__trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 14px;
    background: #fff;
    color: #1f2937;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .pondstat-country-picker__trigger:hover,
  .pondstat-country-picker__trigger--open {
    border-color: #4ECDC4;
    box-shadow: 0 0 0 4px rgba(78, 205, 196, 0.12);
  }
  .pondstat-country-picker__panel {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
    overflow: hidden;
  }
  .pondstat-country-picker__search {
    width: 100%;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 10px 12px;
    font-size: 14px;
    outline: none;
  }
  .pondstat-country-picker__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 180px;
    overflow-y: auto;
  }
  .pondstat-country-picker__option {
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    color: #374151;
    cursor: pointer;
  }
  .pondstat-country-picker__option:hover,
  .pondstat-country-picker__option--active {
    background: #f0fdfa;
    color: #2b7a78;
  }
  .pondstat-country-picker__empty {
    padding: 12px;
    font-size: 13px;
    color: #9ca3af;
    text-align: center;
  }
`;

function CountryPicker({ id, value, onChange }) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedCode = normalizeCountryCode(value);

  const selectedLabel =
    COUNTRY_OPTIONS.find((c) => c.code === selectedCode)?.label || "Select country";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) => c.label.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [search]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(t);
    };
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="pondstat-country-picker" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`pondstat-country-picker__trigger${open ? " pondstat-country-picker__trigger--open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{selectedLabel}</span>
        <Icon icon={open ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"} width={18} />
      </button>
      {open && (
        <div className="pondstat-country-picker__panel">
          <input
            ref={searchRef}
            type="text"
            className="pondstat-country-picker__search"
            placeholder="Search countries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search countries"
          />
          <ul className="pondstat-country-picker__list" role="listbox">
            {filtered.length === 0 ? (
              <li className="pondstat-country-picker__empty">No countries found</li>
            ) : (
              filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={c.code === selectedCode}
                    className={`pondstat-country-picker__option${
                      c.code === selectedCode ? " pondstat-country-picker__option--active" : ""
                    }`}
                    onClick={() => handleSelect(c.code)}
                  >
                    {c.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const StripePaymentSection = ({
  amount,
  renewalAmount = null,
  dueToday = null,
  loading = false,
  disabled = false,
  error = null,
  submitLabel,
  loadingLabel = "Processing payment...",
  onCancel,
  cancelLabel = "Cancel",
  customerName,
  customerEmail,
  country = "US",
  onCountryChange,
  recurringLabel,
  showCountry = true,
  couponCode = "",
  onCouponCodeChange,
  onApplyCoupon,
  couponLoading = false,
  couponMessage = "",
  couponApplied = false,
  couponError = false,
  originalTotal = null,
  discountAmount = 0,
  discountedTotal = null,
  showCoupon = false,
}) => {
  const payLabel =
    submitLabel || `Subscribe $${Number(amount || 0).toFixed(2)}`;
  const showDueToday = dueToday === 0 && renewalAmount != null;
  const showDiscountSummary =
    couponApplied && originalTotal != null && Number(discountAmount) > 0;

  return (
    <>
      <style>{sectionStyles}</style>
      <div className="pondstat-stripe-section mt-4">
        <div className="pondstat-stripe-section__header">
          <div className="pondstat-stripe-section__icon">
            <Icon icon="solar:card-2-bold-duotone" width={26} height={26} />
          </div>
          <div>
            <h3 className="pondstat-stripe-section__title">Payment details</h3>
            <p className="pondstat-stripe-section__subtitle">
              Secure checkout · encrypted by Stripe
            </p>
          </div>
        </div>

        <div className="pondstat-stripe-section__body">
          {showCoupon && onCouponCodeChange && onApplyCoupon && (
            <div className="pondstat-stripe-section__coupon">
              <label className="pondstat-stripe-section__field-label" htmlFor="coupon-code">
                Coupon code
              </label>
              <div className="pondstat-stripe-section__coupon-row">
                <input
                  id="coupon-code"
                  type="text"
                  value={couponCode}
                  onChange={(e) => onCouponCodeChange(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  disabled={loading || couponLoading}
                />
                <button
                  type="button"
                  className="pondstat-stripe-section__coupon-btn"
                  onClick={onApplyCoupon}
                  disabled={loading || couponLoading || !couponCode.trim()}
                >
                  {couponLoading ? "Applying..." : couponApplied ? "Reapply" : "Apply"}
                </button>
              </div>
              {couponMessage && (
                <div
                  className={`pondstat-stripe-section__coupon-msg ${
                    couponError
                      ? "pondstat-stripe-section__coupon-msg--error"
                      : "pondstat-stripe-section__coupon-msg--success"
                  }`}
                >
                  {couponMessage}
                </div>
              )}
              {couponApplied && (
                <p className="small text-muted mb-0 mt-2">
                  Discount applies to your first billing charge. Renewals are at full price.
                </p>
              )}
            </div>
          )}

          {showDiscountSummary && (
            <div className="pondstat-stripe-section__discount-box">
              <div className="pondstat-stripe-section__discount-row pondstat-stripe-section__discount-row--muted">
                <span>Original price</span>
                <span>${Number(originalTotal).toFixed(2)}</span>
              </div>
              <div className="pondstat-stripe-section__discount-row pondstat-stripe-section__discount-row--savings">
                <span>Coupon savings</span>
                <span>-${Number(discountAmount).toFixed(2)}</span>
              </div>
              {discountedTotal != null && dueToday !== 0 && (
                <div className="pondstat-stripe-section__discount-row">
                  <span>First charge</span>
                  <strong>${Number(discountedTotal).toFixed(2)}</strong>
                </div>
              )}
            </div>
          )}

          {(customerName || customerEmail || showDueToday) && (
            <div className="pondstat-stripe-section__billing-box">
              {customerName && (
                <div className="pondstat-stripe-section__billing-row">
                  <span className="text-muted">Name</span>
                  <strong>{customerName}</strong>
                </div>
              )}
              {customerEmail && (
                <div className="pondstat-stripe-section__billing-row">
                  <span className="text-muted">Email</span>
                  <strong>{customerEmail}</strong>
                </div>
              )}
              {showDueToday && (
                <>
                  <div className="pondstat-stripe-section__billing-row">
                    <span className="text-muted">Due today</span>
                    <strong>$0.00</strong>
                  </div>
                  <div className="pondstat-stripe-section__billing-row">
                    <span className="text-muted">After trial</span>
                    <strong>${Number(renewalAmount).toFixed(2)}</strong>
                  </div>
                </>
              )}
              {recurringLabel && (
                <div className="pondstat-stripe-section__recurring">{recurringLabel}</div>
              )}
            </div>
          )}

          {showCountry && onCountryChange && (
            <>
              <label className="pondstat-stripe-section__field-label" htmlFor="billing-country">
                Country
              </label>
              <CountryPicker
                id="billing-country"
                value={country}
                onChange={onCountryChange}
              />
            </>
          )}

          <label className="pondstat-stripe-section__field-label" htmlFor="stripe-card">
            Card information
          </label>
          <div className="pondstat-stripe-section__card-wrap" id="stripe-card">
            <CardElement options={stripeCardElementOptions} />
          </div>

          <div className="pondstat-stripe-section__hints">
            <span>
              <Icon icon="solar:lock-keyhole-bold" width={14} />
              SSL secured
            </span>
            <span>
              <Icon icon="solar:shield-check-bold" width={14} />
              We never store your card
            </span>
          </div>

          <button
            type="submit"
            className="pondstat-stripe-section__pay-btn"
            disabled={loading || disabled}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                {loadingLabel}
              </>
            ) : (
              <>
                <Icon icon="solar:card-send-bold-duotone" width={22} />
                {payLabel}
              </>
            )}
          </button>

          {onCancel && (
            <button type="button" className="pondstat-stripe-section__cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}

          <div className="pondstat-stripe-section__footer">
            <Icon icon="solar:verified-check-bold" width={14} />
            Powered by Stripe
          </div>

          {error && (
            <div className="pondstat-stripe-section__error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StripePaymentSection;
